import React, { useState, useCallback } from "react";
import {
  Stack,
  Text,
  Table,
  Group,
  Avatar,
  LoadingOverlay,
  Alert,
  ActionIcon,
  Badge,
  Button,
  Tooltip,
} from "@mantine/core";
import { StandardTableShell } from "../../common/StandardTable";
import {
  IconAlertCircle,
  IconChevronDown,
  IconChevronRight,
  IconEdit,
  IconTrash,
  IconPlus,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useSalon } from "../../../context/SalonContext";
import { useApp } from "../../../context/AppContext";
import { Employee } from "../../../types/salon";
import { CalculationMethod, CommissionRecord } from "../../../types/commission";
import { filterActiveStaff } from "../../../utils/staffUtils";
import {
  getActiveCommission,
  groupCommissionsByStaff,
  formatCommissionAmount,
  formatCalculationMethod,
  formatBucketTierRates,
  formatTierSummary,
} from "../../../utils/commissionUtils";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import StaffCommissionModal from "../StaffCommissionModal";
import styles from "./CommissionRatesTab.module.css";
import usePermissions from "../../../hooks/usePermissions";
import { useSalonScopedFetch } from "../../../hooks/useSalonScopedFetch";

const methodColor = (method: CalculationMethod): string => {
  switch (method) {
    case "STANDARD":
      return "var(--brand-teal)";
    case "COMMISSION_ONLY":
      return "var(--category-neutral)";
    case "GREATER_THAN":
      return "var(--brand-lime-green)";
    case "NUM_SERVICES":
      return "var(--brand-burnt-orange)";
    case "TIERED":
      return "var(--category-alt)";
    default:
      return "gray";
  }
};

/** The Service/Product column text for a commission row. A TIERED row shows that
 * bucket's rate progression (e.g. "10% → 20%"); every other method shows its
 * flat rate. Returns null when that bucket has nothing configured. Shared by the
 * active row and the expanded history rows so both render tiered rates. */
const bucketDisplay = (
  commission: CommissionRecord | null | undefined,
  bucket: "services" | "products",
): string | null => {
  if (!commission) return null;
  if (commission.calculation_method === "TIERED") {
    return formatBucketTierRates(commission.tiers, bucket);
  }
  return bucket === "services"
    ? formatCommissionAmount(
        commission.service_amount,
        commission.service_amount_type,
      )
    : formatCommissionAmount(
        commission.product_amount,
        commission.product_amount_type,
      );
};

/** Method badge. For a TIERED commission the badge carries a tooltip with the
 * full per-bucket schedule — the only place Packages/Specials rates surface,
 * since the table has no Packages column. */
const MethodBadge: React.FC<{ commission: CommissionRecord }> = ({
  commission,
}) => {
  const badge = (
    <Badge
      size="sm"
      variant="light"
      color={methodColor(commission.calculation_method)}
      style={{ whiteSpace: "nowrap", textAlign: "center" }}
    >
      {formatCalculationMethod(commission.calculation_method)}
    </Badge>
  );
  if (commission.calculation_method !== "TIERED") return badge;
  return (
    <Tooltip label={formatTierSummary(commission.tiers)} withArrow multiline>
      {badge}
    </Tooltip>
  );
};

/**
 * Commissions › Commission Rates. The per-staff commission-rate table (with
 * expandable history and add/edit/delete). Extracted verbatim from the former
 * flat CommissionsSettings page so it can live as a tab under the redesigned,
 * tabbed Commissions page. Page-level access gating is handled by the parent
 * CommissionsManagement wrapper.
 */
const CommissionRatesTab: React.FC = () => {
  const { businessInfo, employees, currentUser } = useSalon() as {
    businessInfo: any;
    employees: Employee[];
    currentUser: any;
  };
  const { getStaffCommissions, deleteCommission } = useApp();
  const perms = usePermissions();
  const commissionsPerms = perms?.settingsCommissions;
  const canEdit = commissionsPerms?.canEdit !== false;

  // Determine scope for filtering which employees to show
  const commissionsScope = commissionsPerms?.scope || "own";
  const currentUserId = currentUser?.id || "";

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [commissionsMap, setCommissionsMap] = useState<
    Record<string, CommissionRecord[]>
  >({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCommission, setEditingCommission] =
    useState<CommissionRecord | null>(null);

  // Delete state
  const [deletingCommissionId, setDeletingCommissionId] = useState<
    string | null
  >(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter active employees, then apply scope-based filtering
  const allActiveEmployees = employees ? filterActiveStaff(employees) : [];

  // When scope is "own", only show current user's commission info
  const activeEmployees =
    commissionsScope === "own" && currentUserId
      ? allActiveEmployees.filter((emp) => emp.id === currentUserId)
      : allActiveEmployees;

  const fetchCommissions = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await getStaffCommissions();
      const grouped = groupCommissionsByStaff(response.commissions);
      setCommissionsMap(grouped);
    } catch (error: any) {
      console.error("Error fetching commissions:", error);
      setLoadError(
        error?.message || "Failed to load commissions. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [getStaffCommissions]);

  // Fetch commissions ONCE per salon (multi-location loop fix). The old
  // effect depended on the businessInfo OBJECT and on fetchCommissions'
  // identity (→ getStaffCommissions → fetchWithAuth), so any upstream identity
  // churn refired it — unbounded when the backend 403s. Failures now stop at
  // loadError with an explicit Retry.
  const { retry: retryCommissions } = useSalonScopedFetch(
    businessInfo?.id,
    fetchCommissions,
    {
      enabled: Boolean(businessInfo && employees && currentUser),
      tag: "commissions",
    },
  );

  const toggleRow = (staffId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(staffId)) {
        next.delete(staffId);
      } else {
        next.add(staffId);
      }
      return next;
    });
  };

  // Add new commission
  const handleAddClick = () => {
    setEditingCommission(null);
    setIsModalOpen(true);
  };

  // Edit existing commission
  const handleEditClick = (commission: CommissionRecord) => {
    setEditingCommission(commission);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingCommission(null);
  };

  const handleSaved = () => {
    fetchCommissions();
  };

  // Delete commission
  const handleDeleteClick = (commissionId: string) => {
    setDeletingCommissionId(commissionId);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingCommissionId) return;
    setIsDeleting(true);
    try {
      await deleteCommission(deletingCommissionId);
      setDeletingCommissionId(null);
      fetchCommissions();
    } catch (error: any) {
      console.error("Error deleting commission:", error);
      setLoadError(
        error?.message || "Failed to delete commission. Please try again.",
      );
      setDeletingCommissionId(null);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <Stack pos="relative" mih={200}>
        <LoadingOverlay visible />
      </Stack>
    );
  }

  return (
    <Stack>
      {loadError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          withCloseButton
          onClose={() => setLoadError(null)}
        >
          <Group justify="space-between" align="center">
            <Text size="sm">{loadError}</Text>
            <Button
              size="compact-xs"
              variant="light"
              color="red"
              onClick={retryCommissions}
            >
              Retry
            </Button>
          </Group>
        </Alert>
      )}

      <Group justify="flex-end" gap="xs" wrap="nowrap">
        {commissionsScope === "own" && (
          <Tooltip
            label="You can only view and manage your own commission settings. Contact a manager to view other staff members' commissions."
            multiline
            w={280}
            withArrow
          >
            <IconInfoCircle
              size={20}
              style={{
                color: "var(--mantine-color-blue-6)",
                cursor: "help",
              }}
            />
          </Tooltip>
        )}
        {canEdit && (
          <Button
            color="teal"
            leftSection={<IconPlus size={16} />}
            onClick={handleAddClick}
          >
            Add New Commission
          </Button>
        )}
      </Group>

      <StandardTableShell minWidth={700} ariaLabel="Staff commissions">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={40} />
            <Table.Th>Staff Member</Table.Th>
            <Table.Th>Effective Start</Table.Th>
            <Table.Th>Service Commission</Table.Th>
            <Table.Th>Product Commission</Table.Th>
            <Table.Th>Method</Table.Th>
            {canEdit && <Table.Th w={80}>Actions</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {activeEmployees.map((employee) => {
            const staffCommissions = commissionsMap[employee.id] || [];
            const active = getActiveCommission(staffCommissions);
            const isExpanded = expandedRows.has(employee.id);
            const pastCommissions = staffCommissions
              .filter((c) => c.id !== active?.id)
              .sort((a, b) =>
                b.effective_start_date.localeCompare(a.effective_start_date),
              );

            const isTiered = active?.calculation_method === "TIERED";
            const serviceDisplay = bucketDisplay(active, "services");
            const productDisplay = bucketDisplay(active, "products");
            // For a tiered row a bucket with no tiers is 0% (see the badge
            // tooltip for the full schedule), not an unconfigured "Not Set".
            const emptyLabel = isTiered ? "—" : "Not Set";

            return (
              <React.Fragment key={employee.id}>
                <Table.Tr className={styles.clickableRow}>
                  <Table.Td
                    onClick={() => toggleRow(employee.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <ActionIcon variant="subtle" size="sm" color="gray">
                      {isExpanded ? (
                        <IconChevronDown size={16} />
                      ) : (
                        <IconChevronRight size={16} />
                      )}
                    </ActionIcon>
                  </Table.Td>
                  <Table.Td onClick={() => toggleRow(employee.id)}>
                    <Group gap="sm" wrap="nowrap">
                      <Avatar size="md" radius="xl" color="teal">
                        {employee.first_name?.[0] || "?"}
                      </Avatar>
                      <Stack
                        gap={2}
                        style={{ whiteSpace: "nowrap", overflowX: "auto" }}
                      >
                        <Text size="sm">
                          {employee.first_name} {employee.last_name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {employee.title}
                        </Text>
                      </Stack>
                    </Group>
                  </Table.Td>
                  <Table.Td onClick={() => toggleRow(employee.id)}>
                    <Text
                      size="sm"
                      c={active?.effective_start_date ? undefined : "dimmed"}
                    >
                      {active?.effective_start_date
                        ? new Date(
                            active.effective_start_date + "T00:00:00",
                          ).toLocaleDateString("en-US", {
                            month: "numeric",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td onClick={() => toggleRow(employee.id)}>
                    <Text
                      size="sm"
                      fw={serviceDisplay ? 500 : undefined}
                      c={serviceDisplay ? undefined : "dimmed"}
                    >
                      {serviceDisplay ?? emptyLabel}
                    </Text>
                  </Table.Td>
                  <Table.Td onClick={() => toggleRow(employee.id)}>
                    <Text
                      size="sm"
                      fw={productDisplay ? 500 : undefined}
                      c={productDisplay ? undefined : "dimmed"}
                    >
                      {productDisplay ?? emptyLabel}
                    </Text>
                  </Table.Td>
                  <Table.Td onClick={() => toggleRow(employee.id)}>
                    {active ? (
                      <MethodBadge commission={active} />
                    ) : (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                  {canEdit && (
                    <Table.Td>
                      {active && (
                        <Group gap={4} wrap="nowrap">
                          <ActionIcon
                            variant="subtle"
                            color="teal"
                            size="sm"
                            onClick={() => handleEditClick(active)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            onClick={() => handleDeleteClick(active.id)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      )}
                    </Table.Td>
                  )}
                </Table.Tr>

                {/* Expanded history rows */}
                {isExpanded &&
                  (pastCommissions.length > 0 ? (
                    pastCommissions.map((commission) => (
                      <Table.Tr
                        key={commission.id}
                        className={styles.expandedRow}
                      >
                        <Table.Td />
                        <Table.Td />
                        <Table.Td>
                          <Text size="sm">
                            {new Date(
                              commission.effective_start_date + "T00:00:00",
                            ).toLocaleDateString("en-US", {
                              month: "numeric",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text
                            size="sm"
                            c={
                              bucketDisplay(commission, "services")
                                ? undefined
                                : "dimmed"
                            }
                          >
                            {bucketDisplay(commission, "services") ?? "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text
                            size="sm"
                            c={
                              bucketDisplay(commission, "products")
                                ? undefined
                                : "dimmed"
                            }
                          >
                            {bucketDisplay(commission, "products") ?? "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <MethodBadge commission={commission} />
                        </Table.Td>
                        {canEdit && (
                          <Table.Td>
                            <Group gap={4} wrap="nowrap">
                              <ActionIcon
                                variant="subtle"
                                color="teal"
                                size="sm"
                                onClick={() => handleEditClick(commission)}
                              >
                                <IconEdit size={16} />
                              </ActionIcon>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                size="sm"
                                onClick={() => handleDeleteClick(commission.id)}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Group>
                          </Table.Td>
                        )}
                      </Table.Tr>
                    ))
                  ) : (
                    <Table.Tr className={styles.expandedRow}>
                      <Table.Td colSpan={canEdit ? 7 : 6}>
                        <Text size="sm" c="dimmed" ta="center">
                          No other commission history found.
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
              </React.Fragment>
            );
          })}
          {activeEmployees.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={canEdit ? 7 : 6}>
                <Text size="sm" c="dimmed" ta="center" py="md">
                  No active staff members found.
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </StandardTableShell>

      {canEdit && (
        <>
          <StaffCommissionModal
            opened={isModalOpen}
            onClose={handleModalClose}
            employees={activeEmployees} // Already filtered by scope
            editingCommission={editingCommission}
            onSaved={handleSaved}
          />
          <DeleteConfirmationModal
            isOpen={deletingCommissionId !== null}
            onClose={() => setDeletingCommissionId(null)}
            onConfirm={handleDeleteConfirm}
            title="Delete Commission"
            message="Are you sure you want to delete this commission record? This action cannot be undone."
            isLoading={isDeleting}
          />
        </>
      )}
    </Stack>
  );
};

export default CommissionRatesTab;
